/**
 * @file 学びの計画 (`/families/{familyId}/study/learning_plans/...`) のクライアント。
 *
 * @what: 学ぶ人 × カリキュラムで1件の計画を、一覧・1件・保存の3経路で読み書きする。保存は取り込み
 *   (最初の保存)・書き換え・終えることを兼ねる。
 * @why: 前の器 (exam_plans) は学ぶ人ごとに1件しか持てず、カリキュラムが増えると片方の保存で他方の
 *   設定が消えた (設計 plan/docs/study_plan_scale_generalization_design.md §5)。
 * @failure: 1件の取得は無ければ not_found (404) なので `null` を返す。他は ApiError を伝播する。
 */

import { ApiError, apiFetch } from "@/shared/api/client";
import type { StudyExamSubjectSetting } from "./examPlans";

/** 計画の状態を表す (active = 進めている / done = 終えた)。 */
export type StudyLearningPlanStatus = "active" | "done";

export interface StudyLearningPlanInput {
  /** カリキュラムの対象 (参考書ルートの target)。 */
  target: string;
  /** 空で送るとサーバが active にする。 */
  status?: StudyLearningPlanStatus;
  /** 取り込んだ日 (YYYY-MM-DD)。空で送るとサーバが最初の保存の日を入れる。 */
  started_at?: string;
  /** 終えた日 (YYYY-MM-DD)。done で空ならサーバが今日を入れる。 */
  ended_at?: string;
  /** 目標の印の名前 (ルートの schools[].name から選ぶ)。 */
  goal_school?: string;
  goal_deviation?: number;
  /** 計画全体の期限 (YYYY-MM-DD)。教養のカリキュラムでは空のまま進める。 */
  exam_date?: string;
  /** YYYY-MM-DD で、無ければ今日を起点にする。 */
  start_date?: string;
  subjects: Record<string, StudyExamSubjectSetting>;
}

export interface StudyLearningPlan extends StudyLearningPlanInput {
  /** "{learner_person_id}__{route_series_id}"。 */
  id: string;
  schema_v: number;
  learner_person_id: string;
  /** どのカリキュラムか (参考書ルートの series_id)。 */
  route_series_id: string;
  status: StudyLearningPlanStatus;
  updated_by_person_id?: string;
  created_at: string;
  updated_at: string;
}

function listPath(familyId: string, personId: string): string {
  return `/families/${encodeURIComponent(familyId)}/study/learning_plans/${encodeURIComponent(personId)}`;
}

function planPath(familyId: string, personId: string, seriesId: string): string {
  return `${listPath(familyId, personId)}/${encodeURIComponent(seriesId)}`;
}

export async function fetchLearningPlans(
  familyId: string,
  personId: string,
  signal?: AbortSignal,
): Promise<StudyLearningPlan[]> {
  const res = await apiFetch<{ plans: StudyLearningPlan[] }>(
    listPath(familyId, personId),
    signal ? { signal } : {},
  );
  return res.plans ?? [];
}

export async function fetchLearningPlan(
  familyId: string,
  personId: string,
  seriesId: string,
  signal?: AbortSignal,
): Promise<StudyLearningPlan | null> {
  try {
    return await apiFetch<StudyLearningPlan>(
      planPath(familyId, personId, seriesId),
      signal ? { signal } : {},
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export function putLearningPlan(
  familyId: string,
  personId: string,
  seriesId: string,
  input: StudyLearningPlanInput,
): Promise<StudyLearningPlan> {
  return apiFetch<StudyLearningPlan>(planPath(familyId, personId, seriesId), {
    method: "PUT",
    body: input,
  });
}

/** 進めている計画だけを返す (終えたものを外す)。 */
export function activeLearningPlans(plans: readonly StudyLearningPlan[]): StudyLearningPlan[] {
  return plans.filter((p) => p.status === "active");
}
